import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import ChartTitle from '../Tabs/ChartTitle';

/**
 * Componente de gráfico de barras para exibir atendimentos por dia.
 * 
 * @param {Object} props
 * @param {string} props.titulo - Título exibido acima do gráfico
 * @param {Array<string>} props.labels - Rótulos do eixo X (dias)
 * @param {Array<number>} props.data - Valores correspondentes aos rótulos (quantidade de atendimentos)
 * @param {string} [props.cor='#667eea'] - Cor da barra (opcional)
 */
const GraficoAtendimentosPorDia = ({
  titulo = 'Atendimentos',
  labels = [],
  data = [],
  cor = '#667eea',
  legendaY = 'Atendimentos'
}) => {
  // Calcula o total de atendimentos
  const totalAtendimentos = data.reduce((acc, curr) => acc + curr, 0);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 2,
        borderRadius: 2,
        backgroundColor: 'background.paper',
        p: 1.5,
        mt: 1.5
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: 200,
          mx: 'auto',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          width: '100%',
        }}
      >
        {/* Label do eixo Y - rotacionado no desktop e normal no mobile */}
        <Typography
          variant="body2"
          sx={{
            transform: { xs: 'none', sm: 'rotate(-90deg)' },
            whiteSpace: 'nowrap',
            mb: { xs: 1, sm: 0 },
            mr: { xs: 0, sm: -6 },
            ml: { xs: 0, sm: -6 },
            fontSize: { xs: '14px', sm: '16px' },
            textAlign: 'center',
          }}
        >
          {legendaY}
        </Typography>

        {/* Linha divisória entre o label Y e o gráfico */}
        <Box
          sx={{
            width: { xs: '100%', sm: '1px' },
            height: { xs: '1px', sm: '100%' },
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            mb: { xs: 1, sm: 0 },
          }}
        />

        {/* Container do gráfico */}
        <Box
          aria-label={`Gráfico de barras: ${titulo}`}
          sx={{
            flex: 1,
            width: '100%',
            minWidth: 0,
          }}
        >
          {/* Componente de título do gráfico */}
          <ChartTitle>{titulo}</ChartTitle>

          {/* Componente de gráfico de barras do MUI X Charts */}
          <BarChart
            series={[{
              data,
              color: cor,
              showLabel: true,
            }]}

            barLabel={(item, context) =>
              context.bar.height < 10 ? null : item.value?.toString()
            }

            xAxis={[{
              id: 'x-axis',
              data: labels,
              scaleType: 'band',
              paddingInner: 0.5,
              paddingOuter: 0.3,
              tickLabelStyle: {
                fill: '#333',
                whiteSpace: 'normal',
                fontSize: labels.length > 8 ? 10 : 12, // Fonte menor para muitos itens
              },
              tickSize: 0,
              label: '',
            }]}

            yAxis={[{
              id: 'y-axis',
              tickLabelStyle: {
                fontSize: 12,
                fill: '#333',
              },
              tickSize: 0,
              label: '',
            }]}

            grid={{ horizontal: false, vertical: false }}
            margin={{ top: 20, right: 30, bottom: 40, left: 50 }}
            height={250}
            borderRadius={10}

            components={{
              Bar: (props) => <rect {...props} rx={15} ry={15} />,
            }}

            sx={{
              '& .MuiBarElement-root': {
                borderRadius: '8px',
              },
               // Garante que todos os ticks sejam exibidos
              '& .MuiChartsAxis-tick': {
                display: 'block !important',
              },
              '& .MuiChartsAxis-tickLabel': {
                display: 'block !important',
              },
            }}
          />
        </Box>
      </Box>

      {/* Rodapé com total de atendimentos */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: 'center',
          justifyContent: { xs: 'space-between', sm: 'center' },
          gap: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: 'text.secondary',
            fontSize: '1rem',
            whiteSpace: 'nowrap'
          }}
        >
          Total de Atentimentos
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#667eea',
            fontSize: '1.1rem',
          }}
        >
          {totalAtendimentos}
        </Typography>
      </Box>
    </Box>
  );
};

export default GraficoAtendimentosPorDia;